/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {packageItemUpsellPersonType, sortPackageItemsByPosition} from "utils/utility";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {useClearUpsells} from "utils/packageUtils";
import {toast} from "react-toastify";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import BookingFooterSteps from "pages/Front/Booking/components/BookingFooterSteps";
import {useGetGroupedUpsells} from "utils/upsellsUtils";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import UpsellGroupNameList from "pages/Front/Booking/BookingUpsells/UpsellGroupNameList";

interface BookingUpsellsParams {}

interface BookingUpsellsProps extends RouteComponentProps<BookingUpsellsParams> {}

const BookingUpsellsNoItems = (props: {onFinish: () => void}) => {
    const {t} = useTranslation();
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (value >= 100) {
            props.onFinish();
            return;
        }

        const interval = setInterval(() => {
            if (value >= 100) {
                props.onFinish();
                clearInterval(interval);
                return;
            }
            setValue(value => value + 1);
        }, 50);
        return () => clearInterval(interval);
    }, [value]);

    return (
        <div className={"flex flex-col items-center justify-center w-full"}>
            <CardComponent className={"flex flex-col items-center justify-center max-w-sm"}>
                <h3 className={"text-lg font-black text-center"}>Upsells</h3>
                <div className={"text-base text-center mb-12"}>{t("No upsells found for selected package.")}</div>
                <div className={"text-sm italic text-center"}>{t("Going to next page automatically")}</div>
                <progress className={"w-full h-1 text-green-100 bg-green-100 my-2"} max={100} value={value} />
            </CardComponent>
        </div>
    );
};

const BookingUpsells = (props: BookingUpsellsProps) => {
    const {t} = useTranslation();
    const packageItemsWithoutGroupOptions = useTypedSelector(state => state.bookingState.bookingProcess?.availablePackageItems?.upsells);
    const sortedPackageItemsList = packageItemsWithoutGroupOptions ? sortPackageItemsByPosition(packageItemsWithoutGroupOptions) : [];
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const bookingFilter = bookingProcess.filter;
    const packageCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const persons = +bookingFilter.adults + +bookingFilter.children;
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const clearPackagesHook = useClearUpsells(bookingProcess);
    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const {bookWithAutomaticTimeslot, isLoading} = useCustomTimeslotsNew(() => {}, props.history, [
        scrollRefTimeslots,
        scrollRefCustomTimeslots,
        scrollRefConfirmTimeslots,
    ]);

    const packageItemsList =
        sortedPackageItemsList.length > 0
            ? sortedPackageItemsList
                  .filter(packageItem => {
                      return packageItemUpsellPersonType(bookingFilter, packageItem.person_type);
                  })
                  .filter(packageItem => {
                      if (packageItem.minimum_persons_count === 0) return true;
                      if (packageItem.minimum_persons_count > 0) {
                          return persons >= packageItem.minimum_persons_count;
                      }
                  })
                  //this is temporary to tidy up upsells
                  .filter(packageItem => packageItem.extra.groupName !== "Competitions")
            : [];

    const submitPackageUpsells = (): void => {
        if (!packageCart || !bookingProcess.selectedTimeslot) {
            toast.error("No packageCart found");
            return;
        }
        bookWithAutomaticTimeslot(null, true, true);
    };

    const {sortedByGroupNames} = useGetGroupedUpsells(packageItemsList);

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    return (
        <div className={"BookingUpsells mx-auto w-full"}>
            {packageItemsList.length > 0 && (
                <>
                    {/*<BookingPackageUpsellsHeader className={"!pt-0"} title={t("Please select your extras")} />*/}
                    <div className={"space-y-4 transform-gpu duration-500"}>
                        <UpsellGroupNameList groupNameItems={sortedByGroupNames} />
                    </div>
                </>
            )}
            {packageItemsList.length === 0 && <BookingUpsellsNoItems onFinish={submitPackageUpsells} />}
            <div className={"flex flex-row justify-center mt-6 w-full"}>
                <BookingFooterSteps nextStepDisabled={false}>
                    <div className={"flex flex-col shrink"}>
                        <ButtonSubmit
                            className={
                                "inline-flex items-center px-4 sm:px-3 py-3 w-auto rounded-md sm:min-w-[210px] !font-medium !text-base sm:!text-lg sm:!leading-none"
                            }
                            icon={"ticket"}
                            id="ButtonSubmitUpsells"
                            isLoading={isLoading}
                            label={selectedUpsells.length === 0 ? t("Continue without upsells") : t("Continue")}
                            onClick={submitPackageUpsells}
                        />
                    </div>
                </BookingFooterSteps>
            </div>
        </div>
    );
};

export default withRouter(BookingUpsells);
